// 认证 API 方法集 / Auth API methods.
// Stage E 重写：由 SSO 外跳切换为本地 email+password 流程 +
// OTP 两步注册 + OTP 两步重置。老的 login(redirectUri) 被移除。
// Stage E rewrite: replaces the SSO redirect flow with local
// email+password login, OTP-gated register, and OTP-gated password reset.
// The old login(redirectUri) has been removed.

import type { ApiClient } from "@/api/client";

export type LanguageTag = "cn" | "en";

// /api/v1/auth/status 的响应结构
// Shape returned by /api/v1/auth/status.
export interface AuthStatus {
  authenticated: boolean;
  provider: string;
  user: null | {
    customer_id: string;
    email: string;
    credit: number;
    is_verified: boolean;
    preferred_language: LanguageTag;
    created_at: string | null;
  };
  capabilities: {
    register: boolean;
    password_reset: boolean;
    change_password: boolean;
    logout: boolean;
  };
}

/**
 * 构造认证 API 方法集（接受注入的 ApiClient 以便测试）。
 * Build the auth API method set (accepts an injected ApiClient for testing).
 */
export function createAuthApi(client: ApiClient) {
  return {
    // 当前会话状态 / Current session status.
    status: (): Promise<AuthStatus> => client.get("/api/v1/auth/status"),

    // 注册第一步：发送 OTP 到邮箱 / Register step 1: send OTP to email.
    registerRequest: (body: { email: string }) =>
      client.post<{ ok: true; resend_after_seconds: number }>(
        "/api/v1/auth/register/request",
        body,
      ),

    // 注册第二步：凭 OTP + 密码完成注册 / Register step 2: finalize with OTP + password.
    register: (body: { email: string; otp: string; password: string }) =>
      client.post<{ ok: true; user: AuthStatus["user"] }>(
        "/api/v1/auth/register",
        body,
      ),

    // 本地账号登录 / Local account login.
    login: (body: { email: string; password: string; remember: boolean }) =>
      client.post<{ ok: true; user: AuthStatus["user"] }>(
        "/api/v1/auth/login",
        body,
      ),

    // 退出登录 / Logout.
    logout: () => client.post<{ ok: true }>("/api/v1/auth/logout", {}),

    // 已登录用户改密 / Change password while authenticated.
    changePassword: (body: { old_password: string; new_password: string }) =>
      client.post<{ ok: true }>("/api/v1/auth/change-password", body),

    // 更新账号偏好 / Update account preferences.
    updatePreferences: (body: { preferred_language: LanguageTag }) =>
      client.patch<{ ok: true; user: AuthStatus["user"] }>(
        "/api/v1/user/preferences",
        body,
      ),

    // 忘记密码第一步：发送 OTP / Password-reset step 1: send OTP.
    passwordResetRequest: (body: { email: string }) =>
      client.post<{ ok: true; resend_after_seconds: number }>(
        "/api/v1/auth/password-reset/request",
        body,
      ),

    // 忘记密码第二步：凭 OTP 设置新密码 / Password-reset step 2: set new password with OTP.
    passwordReset: (body: { email: string; otp: string; new_password: string }) =>
      client.post<{ ok: true }>("/api/v1/auth/password-reset", body),
  };
}

import { api } from "@/api/client";

// 默认单例 / Default singleton bound to the shared ApiClient.
export const authApi = createAuthApi(api);
